type SentryRequestMatcherConfig = {
  currentOrigin: string;
  allowedOrigins: Array<string | undefined>;
  allowedServerDomain?: string;
};

type SentryRequestMatcher = {
  isAllowedRequest: (requestUrl: string) => boolean;
  tracePropagationTargets: RegExp[];
};

const ESCAPED_REGEXP_CHARACTERS = /[.*+?^${}()|[\]\\]/g;
const RELATIVE_URL_PATTERN = /^\/(?!\/)/;
const ABSOLUTE_HTTP_URL_PATTERN = /^https?:\/\//i;

const escapeRegExp = (value: string): string => {
  return value.replace(ESCAPED_REGEXP_CHARACTERS, "\\$&");
};

const parseUrl = (value: string, baseOrigin: string): URL | undefined => {
  try {
    return new URL(value, baseOrigin);
  } catch {
    return undefined;
  }
};

const normalizeOrigin = (value: string | undefined, currentOrigin: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  if (RELATIVE_URL_PATTERN.test(trimmedValue)) {
    return currentOrigin;
  }

  if (!ABSOLUTE_HTTP_URL_PATTERN.test(trimmedValue)) {
    return undefined;
  }

  return parseUrl(trimmedValue, currentOrigin)?.origin;
};

const normalizeServerDomain = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }

  const strippedValue = value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^\.+/, "");
  const host = strippedValue.split("/")[0]?.toLowerCase();
  return host || undefined;
};

const createOriginPattern = (origin: string): RegExp => {
  return new RegExp(`^${escapeRegExp(origin)}(?:[/?#]|$)`, "i");
};

const createServerDomainPattern = (serverDomain: string): RegExp => {
  return new RegExp(`^https?:\\/\\/(?:[^/?#]+\\.)*${escapeRegExp(serverDomain)}(?::\\d+)?(?:[/?#]|$)`, "i");
};

export const createSentryRequestMatcher = ({
  currentOrigin,
  allowedOrigins,
  allowedServerDomain,
}: SentryRequestMatcherConfig): SentryRequestMatcher => {
  const normalizedCurrentOrigin = new URL(currentOrigin).origin;
  const normalizedOriginsSet = new Set<string>([normalizedCurrentOrigin]);

  for (const origin of allowedOrigins) {
    const normalizedOrigin = normalizeOrigin(origin, normalizedCurrentOrigin);
    if (normalizedOrigin) {
      normalizedOriginsSet.add(normalizedOrigin);
    }
  }

  const normalizedOrigins = [...normalizedOriginsSet];
  const normalizedServerDomain = normalizeServerDomain(allowedServerDomain);

  const isAllowedServerDomainHost = (hostname: string): boolean => {
    if (!normalizedServerDomain) {
      return false;
    }

    const normalizedHostName = hostname.toLowerCase();
    return normalizedHostName === normalizedServerDomain || normalizedHostName.endsWith(`.${normalizedServerDomain}`);
  };

  return {
    isAllowedRequest: (requestUrl: string): boolean => {
      const parsedUrl = parseUrl(requestUrl, normalizedCurrentOrigin);
      if (!parsedUrl) {
        return false;
      }

      if (normalizedOriginsSet.has(parsedUrl.origin)) {
        return true;
      }

      return isAllowedServerDomainHost(parsedUrl.hostname);
    },
    tracePropagationTargets: [
      RELATIVE_URL_PATTERN,
      ...normalizedOrigins.map((origin) => createOriginPattern(origin)),
      ...(normalizedServerDomain ? [createServerDomainPattern(normalizedServerDomain)] : []),
    ],
  };
};
